import { useState, useEffect, useCallback } from "react";
import { Card } from "ktm-ui";
import { Spinner, Table } from "react-bootstrap";
import toastr from "toastr";
import moment from "moment";
import CustomPagination from "components/CustomPagination";
import CustomToolTip from "components/CustomToolTip";
import BulkEditEmployeeBenCatFixedCampaignModal from "components/Bulk_edit_employee_bencat_fixed_campaign";
import EditIndividualEmpFixedCampaignModal from "components/Edit_individual_emp_fixed_campaign";

const ITEMS_PER_PAGE = 20;

const FixedEmployeeCampaignTable = ({ policy_id, benefitEndDate, HasAccrual }) => {
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [hasSalaryBaseBenefits, setHasSalaryBaseBenefits] = useState(false);

    const [showHsaLimit, setShowHsaLimit] = useState(false);
    const [showLsaLimit, setShowLsaLimit] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [bencatOptions, setBencatOptions] = useState([]);

    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [sortConfig, setSortConfig] = useState<
        { field: string; direction: "asc" | "desc" }[]
    >([]);

    const fetchEmployeeCampaigns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `/api/FixedCampaign/getemployeefixedcampaigns?policyId=${policy_id}`
            );
            const data = await res.json();

            if (!data.success) {
                toastr.error(data.message ?? "Failed to load campaigns");
                return;
            }

            setEmployees(data.employeeCampaigns);
            setHasSalaryBaseBenefits(data.hasSalaryBaseBenefits);
            setShowHsaLimit(data.showHsaLimit);
            setShowLsaLimit(data.showLsaLimit);
        } catch {
            toastr.error("Failed to fetch employee campaigns");
        } finally {
            setLoading(false);
        }
    }, [policy_id]);

    const fetchBenefitCategories = useCallback(async () => {
        try {
            const res = await fetch(
                `/api/Utility/getnextyearbenefitcategories?policyId=${policy_id}`
            );
            const data = await res.json();

            if (data.success) {
                setBencatOptions([
                    { Id: null, name: "--Select--", CreditCalcMethod: null },
                    ...data.benefitCategories
                ]);
            }
        } catch {
            toastr.error("Failed to fetch benefit categories");
        }
    }, [policy_id]);

    useEffect(() => {
        fetchEmployeeCampaigns();
        fetchBenefitCategories();
    }, [fetchEmployeeCampaigns, fetchBenefitCategories]);

    const handleSort = (field: string) => {
        setSortConfig(prev => {
            const existing = prev.find(s => s.field === field);

            if (!existing) {
                return [{ field, direction: "asc" }, ...prev];
            }

            if (existing.direction === "asc") {
                return prev.map(s =>
                    s.field === field ? { ...s, direction: "desc" } : s
                );
            }

            return prev.filter(s => s.field !== field);
        });
    };

    const multiSort = (data, config) => {
        if (!config.length) return data;

        const sorted = [...data];
        sorted.sort((a, b) => {
            for (const { field, direction } of config) {
                const aVal = a[field] ?? "";
                const bVal = b[field] ?? "";

                if (aVal < bVal) return direction === "asc" ? -1 : 1;
                if (aVal > bVal) return direction === "asc" ? 1 : -1;
            }
            return 0;
        });

        return sorted;
    };

    const renderSortIndicator = (field: string) => {
        const sorter = sortConfig.find(s => s.field === field);
        if (!sorter) return null;

        return (
            <span className="RT-DataTable__header-sort-icon">
                {sorter.direction === "asc" ? <SortAsc /> : <SortDesc />}
            </span>
        );
    };

    const sortedEmployees = multiSort(employees, sortConfig);

    const currentRecords = sortedEmployees.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const nPages = Math.ceil(sortedEmployees.length / ITEMS_PER_PAGE);

    const handleSelectAll = () => {
        setSelectedEmployees(prev =>
            prev.length === employees.length ? [] : employees.map(e => e.id)
        );
    };

    return (
        <div className="employee-campaigns-container">
            <Card>
                {showBulkEditModal && (
                    <BulkEditEmployeeBenCatFixedCampaignModal
                        showBulkEditEmployeeBenCatFixedCampaignModal
                        bencatOptions={bencatOptions}
                        policyId={policy_id}
                        employeeIds={selectedEmployees}
                        closeBulkEditEmployeeBenCatFixedCampaignModal={() => {
                            setShowBulkEditModal(false);
                            setSelectedEmployees([]);
                            fetchEmployeeCampaigns();
                        }}
                    />
                )}

                {showEditModal && (
                    <EditIndividualEmpFixedCampaignModal
                        showEditIndividualEmpFixedCampaignModal
                        bencatOptions={bencatOptions}
                        policyId={policy_id}
                        employee={selectedEmployee}
                        closeEditIndividualEmpFixedCampaignModal={() => {
                            setShowEditModal(false);
                            fetchEmployeeCampaigns();
                        }}
                    />
                )}

                <div className="bulk-actions-toolbar">
                    <button className="btn btn-secondary" onClick={handleSelectAll}>
                        Select / Deselect All
                    </button>

                    <button
                        className="btn btn-primary"
                        disabled={!selectedEmployees.length}
                        onClick={() => setShowBulkEditModal(true)}
                        style={{ marginLeft: 10 }}
                    >
                        Bulk Edit Category
                    </button>
                </div>

                {loading ? (
                    <Spinner animation="border" />
                ) : (
                    <>
                        <Table hover>
                            <thead>
                                <tr>
                                    <th />

                                    <th onClick={() => handleSort("lastName")} className="sortable-header">
                                        Last Name {renderSortIndicator("lastName")}
                                    </th>

                                    <th onClick={() => handleSort("firstName")} className="sortable-header">
                                        First Name {renderSortIndicator("firstName")}
                                    </th>

                                    <th onClick={() => handleSort("email")} className="sortable-header">
                                        Email {renderSortIndicator("email")}
                                    </th>

                                    <th onClick={() => handleSort("name")} className="sortable-header">
                                        Benefit Category {renderSortIndicator("name")}
                                    </th>

                                    {HasAccrual && (
                                        <th onClick={() => handleSort("isScheduled")} className="sortable-header">
                                            Is Scheduled {renderSortIndicator("isScheduled")}
                                            <CustomToolTip
                                                header="Is Scheduled"
                                                bodyContent="Employee receives Scheduled Credits throughout the year."
                                            />
                                        </th>
                                    )}

                                    {hasSalaryBaseBenefits && (
                                        <>
                                            <th onClick={() => handleSort("creditCalcMethod")} className="sortable-header">
                                                Calc Method {renderSortIndicator("creditCalcMethod")}
                                            </th>

                                            <th onClick={() => handleSort("salaryMoney")} className="sortable-header">
                                                Salary {renderSortIndicator("salaryMoney")}
                                            </th>
                                        </>
                                    )}

                                    <th onClick={() => handleSort("coverage")} className="sortable-header">
                                        Coverage {renderSortIndicator("coverage")}
                                    </th>

                                    {showHsaLimit && (
                                        <th onClick={() => handleSort("hsalimit")} className="sortable-header">
                                            HSA Limit {renderSortIndicator("hsalimit")}
                                        </th>
                                    )}

                                    {showLsaLimit && (
                                        <th onClick={() => handleSort("lsalimit")} className="sortable-header">
                                            LSA Limit {renderSortIndicator("lsalimit")}
                                        </th>
                                    )}

                                    {moment(benefitEndDate).isAfter(moment()) && <th />}
                                </tr>
                            </thead>

                            <tbody>
                                {currentRecords.map(emp => (
                                    <tr key={emp.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedEmployees.includes(emp.id)}
                                                onChange={() =>
                                                    setSelectedEmployees(prev =>
                                                        prev.includes(emp.id)
                                                            ? prev.filter(id => id !== emp.id)
                                                            : [...prev, emp.id]
                                                    )
                                                }
                                            />
                                        </td>

                                        <td>{emp.lastName}</td>
                                        <td>{emp.firstName}</td>
                                        <td>{emp.email}</td>
                                        <td>{emp.name}</td>

                                        {HasAccrual && (
                                            <td>{emp.isScheduled ? "Yes" : "No"}</td>
                                        )}

                                        {hasSalaryBaseBenefits && (
                                            <>
                                                <td>{emp.creditCalcMethod}</td>
                                                <td>
                                                    {emp.creditCalcMethod === "SALARY"
                                                        ? emp.salaryMoney
                                                        : "N/A"}
                                                </td>
                                            </>
                                        )}

                                        <td>{emp.coverage}</td>

                                        {showHsaLimit && (
                                            <td>{"$ " + emp.hsalimit.toLocaleString()}</td>
                                        )}

                                        {showLsaLimit && (
                                            <td>{"$ " + emp.lsalimit.toLocaleString()}</td>
                                        )}

                                        {moment(benefitEndDate).isAfter(moment()) && (
                                            <td>
                                                <a
                                                    href="#"
                                                    className="RT-Link"
                                                    onClick={() => {
                                                        setSelectedEmployee(emp);
                                                        setShowEditModal(true);
                                                    }}
                                                >
                                                    Edit
                                                </a>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <CustomPagination
                            nPages={nPages}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                        />
                    </>
                )}
            </Card>
        </div>
    );
};

export default FixedEmployeeCampaignTable;

const SortAsc = () => (
    <svg width="14" height="14" viewBox="0 0 24 24">
        <path d="M12 4l-6 6h12z" />
    </svg>
);

const SortDesc = () => (
    <svg width="14" height="14" viewBox="0 0 24 24">
        <path d="M12 20l6-6H6z" />
    </svg>
);
